import { Injectable } from '@angular/core';
import { ActivatedRoute, NavigationExtras, Router } from '@angular/router';
import { AuthenticationService } from '@core/authentication/authentication.service';
import { Option } from '@core/interfaces';
import { endOfDay, mapIds, startOfDay } from '@core/utils/filters-utils';
import { isEmpty } from 'lodash';
import moment from 'moment';
import { CriteriaGroupOption } from '../interfaces';

@Injectable({
  providedIn: 'root'
})
export class FilterStateManagerService {

  public selectedOption: CriteriaGroupOption;
  public isFilterOn = false;

  constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly authenticationService: AuthenticationService,
  ) { }

  public toQuery(value: any): Object {
    return {
      locationGroupId_in: mapIds(value.locationGroups),
      locationId_in: mapIds(value.locations),
      typeId_in: mapIds(value.maintenanceTypes),
      segmentId_in: mapIds(value.segments),
      companyId_in: mapIds(value.companies),
      from: startOfDay(value.openedAtGte),
      to: endOfDay(value.openedAtLte),
      scheduledAt_gte: startOfDay(value.scheduledAtGte, 'YYYY-MM-DD'),
      scheduledAt_lte: endOfDay(value.scheduledAtLte, 'YYYY-MM-DD'),
      closedAt_gte: startOfDay(value.closedAtGte),
      closedAt_lte: endOfDay(value.closedAtLte),
    };
  }

  public clearFilters(filter, routeName): void {
    const queryParams = this.router.routerState.snapshot.root.queryParams;
    let newQueryParams: Object = {};
    const { filters, ...restQueryParams } = queryParams;
    newQueryParams = restQueryParams;

    const url = this.router.serializeUrl(this.router.createUrlTree([routeName], { queryParams: { ...newQueryParams } }));
    filter.filtersForm.patchValue(filter.defaultFilterValues);
    this.isFilterOn = false;
    this.setDataOnLocalStorage(filter, routeName);

    this.router.navigateByUrl(url);
  }

  public async getSavedData(filter, routeName): Promise<void> {
    if (filter.filterForm && filter.filtersForm.invalid) {
      return;
    }

    const dataFromUrl = this.route.snapshot.queryParams;

    if (isEmpty(dataFromUrl)) {
      this.getDataFromLocalStorage(filter, routeName);
      this.setDataToQueryParams(filter, routeName);
      return;
    }

    this.getDataFromUrl(filter, dataFromUrl);
  }

  public setDataToQueryParams(filter, routeName): void {
    if (filter.filterForm && filter.filtersForm.invalid) {
      return;
    }

    const userId = this.authenticationService.user.id;
    const formatedFilters = this.removeNullProperties(filter.filtersForm.value);
    const navigationQuery: NavigationExtras = this.isFilterOn ? {
      queryParams: {
        userId: userId,
        filters: this.isFilterOn ? JSON.stringify(formatedFilters) : null,
        selectedOption: this.selectedOption ? JSON.stringify(this.selectedOption) : null
      },
      queryParamsHandling: 'merge'
    } : null;

    const url = navigationQuery ? this.router.serializeUrl(
      this.router.createUrlTree([routeName], { queryParams: { ...navigationQuery.queryParams } })
    ) : routeName;
    this.router.navigateByUrl(url);
  }

  public setDataOnLocalStorage(filter, routeName): void {
    if (this.authenticationService && this.authenticationService.user && this.authenticationService.user.id && filter.filtersForm.valid) {
      const userId = this.authenticationService.user.id;
      const data = this.isFilterOn ? {
        userId: userId,
        filters: this.isFilterOn ? filter.filtersForm.value : null,
        selectedOption: this.selectedOption ? this.selectedOption : null
      } : null;

      localStorage.setItem(routeName, JSON.stringify(data));
    }
  }

    private getDataFromLocalStorage(filter, routeName): void {
    const dataFromLocalStorage = JSON.parse(localStorage.getItem(routeName));
    const isUserValidLocalStorage = dataFromLocalStorage && dataFromLocalStorage.userId === this.authenticationService.user.id;
    const savedSelectedOptionLocalStorage = dataFromLocalStorage && dataFromLocalStorage.selectedOption;
    const savedFiltersLocalStorage = dataFromLocalStorage && dataFromLocalStorage.filters;

    if (!isUserValidLocalStorage) {
      return;
    }

    if (savedSelectedOptionLocalStorage) {
      const selectedOption = savedSelectedOptionLocalStorage;
      this.selectedOption = selectedOption;
    }

    if (savedFiltersLocalStorage) {
      const formatedFilters = this.convertStringDateToMomentDate(savedFiltersLocalStorage);
      filter.filtersForm.patchValue(formatedFilters);
      this.isFilterOn = true;
    }
  }

  private getDataFromUrl(filter, dataFromUrl): void {
    const isUserValidUrl = dataFromUrl.userId === this.authenticationService.user.id;
    const selectedOptionFromUrl = dataFromUrl.selectedOption ? JSON.parse(dataFromUrl.selectedOption) : null;
    const filtersFromUrl = dataFromUrl.filters ? JSON.parse(dataFromUrl.filters) : null;

    if (!isUserValidUrl) {
      return;
    }

    if (selectedOptionFromUrl) {
      const selectedOption = selectedOptionFromUrl;
      this.selectedOption = selectedOption;
    }

    if (filtersFromUrl) {
      const formatedFilters = this.convertStringDateToMomentDate(filtersFromUrl);
      filter.filtersForm.patchValue(formatedFilters);
      this.isFilterOn = true;
    }
  }

  private removeNullProperties(obj: any): any {
    return Object.entries(obj)
      .filter(([_, value]) => value !== null)
      .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});
  }

  private convertStringDateToMomentDate(filtersFromUrl): any {
    const keysToConvert = ['openedAtGte', 'openedAtLte', 'closedAtGte', 'closedAtLte', 'scheduledAtGte', 'scheduledAtLte'];
    keysToConvert.forEach((key) => {
      if (filtersFromUrl[key]) {
        filtersFromUrl[key] = moment(filtersFromUrl[key]);
      }
    });

    return filtersFromUrl;
  }
}
